import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, Zap, Clock, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Ruby / Rails', icon: Server },
  { name: 'PostgreSQL / SQL Server', icon: Database },
  { name: 'Sidekiq / Redis', icon: Cloud },
  { name: 'React + TypeScript', icon: FileText },
  { name: 'Datadog / PagerDuty', icon: Shield },
  { name: 'Jenkins / GitHub Actions', icon: Zap },
];

const keyMetrics = [
  {
    icon: Database,
    value: '88',
    label: 'Microservices Audited',
    description: 'Fully mapped and dependency-analyzed across three platform domains',
  },
  {
    icon: Zap,
    value: '700K+',
    label: 'Daily Prior Auths',
    description: 'Peak volume processed by the audited platform',
  },
  {
    icon: Clock,
    value: '8 Weeks',
    label: 'Full Assessment',
    description: 'Architecture, reliability, security, and compliance delivered',
  },
  {
    icon: Shield,
    value: 'HIPAA',
    label: 'Risks Identified',
    description: 'PHI exposure, race conditions, and reliability gaps surfaced',
  },
];

const highlights = [
  {
    category: 'Audit Delivery',
    items: [
      'Completed end-to-end assessment of all 88 microservices across 8 focus areas in 8 weeks',
      'Delivered a prioritized remediation roadmap covering reliability, performance, security, and HIPAA compliance',
      'Produced full architectural documentation including end-to-end PA lifecycle map with 19/20 steps code-confirmed',
    ],
  },
  {
    category: 'Critical Risk Identification',
    items: [
      'Identified active PHI exposure in retrospective_pa_created event payload travelling Azure Service Bus to 4 subscribers — flagged RED for immediate remediation',
      'Surfaced ePAmotron retry logic gap and circuit breaker absence on RxService → Claims path as critical reliability risks',
      'Documented CMM2 multi-writer race condition across 5+ services with no transactional coordination',
    ],
  },
];

const challenges = [
  {
    title: 'Mapping 88 Services Without Existing Documentation',
    icon: Database,
    problem: 'No comprehensive architectural documentation existed for the 88-service platform — understanding dependencies, data flows, and failure modes required reconstructing the full architecture from code, APIs, and engineering interviews.',
    solution: 'Systematically analyzed each service\'s inputs, outputs, database interactions, and cross-service dependencies through code review, Confluence documentation, and structured workshops with 13 engineering teams.',
    impact: 'First-ever documented architecture map of the complete Access Engineering platform',
  },
  {
    title: 'Identifying PHI Compliance Exposure Across Distributed Services',
    icon: Shield,
    problem: 'PHI data flows across 88 services with no centralised compliance documentation, making it impossible to assess regulatory exposure without systematic service-by-service analysis of all event payloads and data paths.',
    solution: 'Conducted systematic PHI data flow tracing across all services, identifying an active exposure in the retrospective_pa_created event payload carrying patient DOB, name, and prescriber NPI across Azure Service Bus to 4 subscribers.',
    impact: 'Active HIPAA risk (RSK-PHI-01) surfaced with specific code evidence and immediate remediation guidance',
  },
  {
    title: 'Reliability Risks in 600–700K Daily Volume Processing',
    icon: Zap,
    problem: 'The platform processes 600–700K prior authorizations at peak daily — unhandled retry logic gaps and absent circuit breakers represented critical reliability risks at this volume that had never been formally documented.',
    solution: 'Identified ePAmotron silent failure on dead-strategy PAs and RxService → Claims path reliability gap through systematic code review, producing specific engineering recommendations with file and line-level evidence.',
    impact: 'Critical reliability risks surfaced and prioritized in the remediation roadmap before Azure migration begins',
  },
  {
    title: 'Portal-Services Behavioral Contradictions',
    icon: FileText,
    problem: 'Inconsistencies between portal-facing behavior and backend service logic created unpredictable outcomes for prior authorization requests — but had never been formally documented or communicated across engineering teams.',
    solution: 'Produced a dedicated Portal-Services contradiction memo identifying specific behavioral mismatches with root cause analysis, code evidence, and recommended resolution approach for each inconsistency found.',
    impact: 'Contradictions formally documented and incorporated into the engineering remediation roadmap',
  },
];

const HealthcareAudit = () => {
  return (
    <>
      <SEOHead
        title="Access Engineering Application Audit Case Study | Agility AI"
        description="How Agility delivered an 8-week audit of 88 Ruby/Rails microservices processing 600-700K daily prior authorizations — surfacing HIPAA risks, reliability gaps, and producing a full remediation roadmap."
        url="https://www.agilitytech.ai/case-studies/healthcare-audit"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Access Engineering Application Audit",
          "description": "How Agility delivered an 8-week audit of 88 Ruby/Rails microservices processing 600-700K daily prior authorizations — surfacing HIPAA risks, reliability gaps, and producing a full remediation roadmap.",
          "author": { "@type": "Organization", "name": "Agility" },
          "publisher": {
            "@type": "Organization",
            "name": "Agility",
            "logo": { "@type": "ImageObject", "url": "https://www.agilitytech.ai/agility hex fonts.png" }
          },
          "datePublished": "2025-10-01",
          "dateModified": "2026-05-01",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.agilitytech.ai/case-studies/healthcare-audit" }
        }}
      />
      <div className="pt-20 bg-black">
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80" alt="Banner" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Healthcare</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">Access Engineering Application Audit</h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">Delivering a comprehensive 8-week audit of 88 Ruby/Rails microservices processing 600–700K daily prior authorizations, producing a prioritized reliability, performance, and HIPAA compliance remediation roadmap.</p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3><p className="text-white">Healthcare</p></div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3><p className="text-white">USA</p></div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3><p className="text-white">Enterprise Platform Audit — 88-Service Architecture</p></div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3><p className="text-white">8-Week Assessment Engagement</p></div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Auditing a Mission-Critical Healthcare Authorization Platform</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">A leading healthcare technology platform — 88 Ruby/Rails microservices processing 600–700K prior authorizations daily at peak — had never undergone a formal comprehensive architecture audit. Operating across three primary domains (Portal, Payer Integrations, Pharmacy Services), the platform carried undocumented reliability risks, no end-to-end distributed tracing, inconsistent monitoring standards across 13 engineering teams, no production-like test environment, and active HIPAA/PHI compliance exposure. With a planned migration to Azure and Kubernetes on the horizon, leadership needed a complete evidence-based assessment before any transformation began.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">First Comprehensive Platform Assessment</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">We delivered the first-ever end-to-end architectural assessment of all 88 microservices in 8 weeks — covering reliability, performance, HIPAA compliance, and security. The engagement produced a full PA lifecycle map (19/20 steps code-confirmed), surfaced an active PHI exposure in production event payloads, identified critical reliability gaps in high-volume processing paths, and delivered a prioritized remediation roadmap giving engineering and leadership an evidence-based foundation for the upcoming Azure and Kubernetes migration.</p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center"><span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>Problem</h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>Solution</h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>
                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">Ready to Audit Your Healthcare Platform?</h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">Contact our engineering assessment team to discover how a structured audit can surface compliance risks, reliability gaps, and architectural issues before they become production incidents.</p>
              <Link to="/contact" className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HealthcareAudit;
